import React, { useState } from "react";
import { Table, Input, Form, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

interface InstancePublicKey {
  id: string;
  instanceName: string;
  ipAddress: string;
  sshPublicKey: string;
}

const initialData: InstancePublicKey[] = [
  {
    id: "1",
    instanceName: "Instance A",
    ipAddress: "192.168.1.1",
    sshPublicKey: "PublicKey123",
  },
  {
    id: "2",
    instanceName: "Instance B",
    ipAddress: "192.168.1.2",
    sshPublicKey: "securepass456",
  },
];

export const SSHPublicKeyTable: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [data, setData] = useState<InstancePublicKey[]>(initialData);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const isEditing = (record: InstancePublicKey) => record.id === editingKey;

  const edit = (record: InstancePublicKey) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey(null);
  };

  const save = async (id: string) => {
    try {
      const row = (await form.validateFields()) as InstancePublicKey;
      const newData = [...data];
      const index = newData.findIndex((item) => item.id === id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setData(newData);
        setEditingKey(null);
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns: ColumnsType<InstancePublicKey> = [
    {
      title: t("Instance Name"),
      dataIndex: "instanceName",
      key: "instanceName",
    },
    {
      title: t("IP Address"),
      dataIndex: "ipAddress",
      key: "ipAddress",
    },
    {
      title: t("SSH Public Key"),
      dataIndex: "sshPublicKey",
      key: "sshPublicKey",
      render: (_: any, record: InstancePublicKey) => {
        const editable = isEditing(record);
        return editable ? (
          <>
            <Form.Item
              name="sshPublicKey"
              style={{ margin: 0 }}
              rules={[{ required: true, message: "Please input public key!" }]}
            >
              <Input.Password />
            </Form.Item>
            <button
              className="link-btn"
              onClick={() => save(record.id)}
              style={{ marginRight: 8 }}
            >
              {t("Save")}
            </button>
            <button className="link-btn" onClick={cancel}>
              {t("cancel")}
            </button>
          </>
        ) : (
          <>
            <>*************</>
            <button
              className="link-btn"
              disabled={editingKey !== null}
              onClick={() => edit(record)}
            >
              {t("Edit")}
            </button>
          </>
        );
      },
    },
  ];

  return (
    <Form form={form} component={false}>
      <Table<InstancePublicKey>
        dataSource={data}
        columns={columns}
        rowKey="id"
        pagination={false}
      />
    </Form>
  );
};
